import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import {
  IZoomAware,
  IStatefulEntity,
  UpdateDataResult,
} from '../core/interfaces/entity-layer-manager.interface';
import { ILuminaria } from 'modelos/src';
import {
  CodigoEstadoLuminaria,
  determinarCodigoEstado,
  presentacionEstado,
} from '../../../modulos/entidades/luminarias/estado/estado-luminaria';
import { Punto, puntoValido } from '../ubicacion-valida';

const CODIGOS: CodigoEstadoLuminaria[] = [0, 1, 2, 3, 4, 5];

// clusterProperties: cuenta cuántos puntos de cada código hay en el cluster.
// Habilita el donut multicolor proporcional.
const CLUSTER_COUNT_PROPERTIES = CODIGOS.reduce(
  (acc, codigo) => {
    acc[`count_${codigo}`] = [
      '+',
      ['case', ['==', ['get', 'estado'], codigo], 1, 0],
    ];
    return acc;
  },
  {} as Record<string, unknown>,
);

/**
 * LuminariasLayerManager
 *
 * Manager para renderizar luminarias en el mapa.
 *
 * Características COMPLEJAS:
 * - Estados dinámicos (6 estados diferentes con iconos específicos)
 * - Zoom threshold: < 14 muestra círculos, >= 14 muestra iconos
 * - Dos modos: mapa específico (iconos .webp) vs mapa general (pin fijo)
 * - Soporta clustering
 * - Color de cluster: amarillo (#fad66a)
 *
 * Implementa:
 * - IZoomAware: Reacciona a cambios de zoom
 * - IStatefulEntity: Maneja estados dinámicos
 */
@Injectable({
  providedIn: 'root',
})
export class LuminariasLayerManager
  extends BaseEntityLayerManager<ILuminaria>
  implements IZoomAware, IStatefulEntity<CodigoEstadoLuminaria>
{
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'luminarias';
  readonly supportsClustering = true;
  readonly clusterColor = '#fad66a'; // Amarillo
  readonly zoomThreshold = 14; // Similar a OpenLayers: transición de círculos a iconos

  // IDs de layers
  private iconLayerId = 'luminarias-icon-layer';
  private circleLayerId = 'luminarias-circle-layer';
  private unclusteredLayerId = 'luminarias-unclustered-layer';

  // Estado interno
  private currentZoom = 15; // Por defecto, mostrar iconos
  private mapaEspecifico = false; // Por defecto, usar pin fijo (mapa general)
  private zoomHandler?: () => void;

  // Coordenadas finales (con offset) por id de luminaria, para separar las que
  // comparten la misma posición (ej: misma puesta → misma ubicación).
  private overlapCoords = new Map<string, [number, number]>();
  // Separación deseada (en metros) entre luminarias superpuestas.
  private readonly OVERLAP_SEP_METERS = 8;
  private readonly OVERLAP_MIN_RADIUS_METERS = 3;

  // Donut markers (cluster multicolor): cache por cluster_id y handler de render.
  private donutMarkers: Record<string, mapboxgl.Marker> = {};
  private donutMarkersOnScreen: Record<string, mapboxgl.Marker> = {};
  private renderHandler?: () => void;

  // ========== MAPEO DE CÓDIGOS A ICONOS/COLORES ==========
  // Derivado de la fuente única presentacionEstado (estado-luminaria.ts).

  readonly stateToIconMap = {
    0: presentacionEstado(0).iconoMapa,
    1: presentacionEstado(1).iconoMapa,
    2: presentacionEstado(2).iconoMapa,
    3: presentacionEstado(3).iconoMapa,
    4: presentacionEstado(4).iconoMapa,
    5: presentacionEstado(5).iconoMapa,
  } as Record<CodigoEstadoLuminaria, string>;

  // Color por código para los círculos en zoom bajo.
  readonly stateToColorMap = {
    0: presentacionEstado(0).color,
    1: presentacionEstado(1).color,
    2: presentacionEstado(2).color,
    3: presentacionEstado(3).color,
    4: presentacionEstado(4).color,
    5: presentacionEstado(5).color,
  } as Record<CodigoEstadoLuminaria, string>;

  // Tamaños de visualización
  private readonly CIRCLE_RADIUS = 4; // Radio de círculos en zoom bajo (4px = puntos pequeños)
  private readonly ICON_SIZE_ESPECIFICO = 0.08; // .webp 500x500 → 40px (pines pequeños)
  private readonly ICON_SIZE_GENERAL = 0.21; // pin 165x165 → 35px (consistente con OpenLayers)

  // ========== CONFIGURACIÓN DE MODO ==========

  /**
   * Configura si se usa mapa específico (iconos .webp) o general (pin fijo)
   */
  setMapaEspecifico(especifico: boolean): void {
    this.mapaEspecifico = especifico;

    // Si ya está inicializado, actualizar las expresiones de los layers
    if (this.isInitialized && this.map) {
      this.updateIconExpressions();
      this.updateClusterStyle();
      // Refrescar visibilidad de cluster (alterna entre donut multicolor y circle amarillo)
      if (this.clusteringEnabled) {
        this.updateClusteringVisibility();
      }
    }
  }

  /**
   * Actualiza las expresiones de iconos cuando cambia mapaEspecifico
   */
  private updateIconExpressions(): void {
    if (!this.map) return;

    // Actualizar currentZoom con el valor real del mapa
    this.currentZoom = this.map.getZoom();
    const iconExpression = this.getIconExpression();

    // Actualizar icon layer
    if (this.map.getLayer(this.iconLayerId)) {
      this.map.setLayoutProperty(
        this.iconLayerId,
        'icon-image',
        iconExpression,
      );
      this.map.setLayoutProperty(
        this.iconLayerId,
        'icon-size',
        this.mapaEspecifico
          ? this.ICON_SIZE_ESPECIFICO
          : this.ICON_SIZE_GENERAL,
      );
    }

    // Actualizar unclustered layer
    if (this.map.getLayer(this.unclusteredLayerId)) {
      this.map.setLayoutProperty(
        this.unclusteredLayerId,
        'icon-image',
        iconExpression,
      );
      this.map.setLayoutProperty(
        this.unclusteredLayerId,
        'icon-size',
        this.mapaEspecifico
          ? this.ICON_SIZE_ESPECIFICO
          : this.ICON_SIZE_GENERAL,
      );
    }

    // IMPORTANTE: Actualizar visibilidad según zoom actual solo si clustering OFF
    // Si clustering ON, los layers normales deben permanecer ocultos
    if (!this.clusteringEnabled) {
      this.updateZoomBasedVisibility();
    }
  }

  // ========== SEPARACIÓN DE POSICIONES SUPERPUESTAS ==========

  /**
   * Dónde se dibuja una luminaria: su ubicación o, si no tiene una real, la de
   * su puesta (si vino poblada). `null` = no se dibuja. Antes las que no tenían
   * ubicación, o tenían el valor por defecto de un selector de mapa, se
   * dibujaban todas en Buenos Aires.
   */
  private ubicacionEnMapa(luminaria: ILuminaria): Punto | null {
    const puesta = (
      luminaria as ILuminaria & {
        puesta?: { ubicacion?: { coordinates?: unknown } | null } | null;
      }
    ).puesta;
    return puntoValido(luminaria.ubicacion) ?? puntoValido(puesta?.ubicacion);
  }

  /**
   * Coordenada base de una luminaria (sin offset). Sólo se llama con las que
   * pasaron el filtro de updateData.
   */
  private getBaseCoords(luminaria: ILuminaria): [number, number] {
    return this.ubicacionEnMapa(luminaria) ?? [0, 0];
  }

  /**
   * Calcula coordenadas con offset para las luminarias que comparten la misma
   * posición (ej: varias luminarias en una misma puesta con una única ubicación).
   * Las distribuye en un círculo alrededor del punto real para diferenciarlas.
   * El orden es estable (por id) para que no salten entre actualizaciones.
   */
  private computeOverlapOffsets(entities: ILuminaria[]): void {
    this.overlapCoords.clear();

    // Agrupar por coordenada (redondeada a ~0.1m para detectar misma posición)
    const grupos = new Map<string, ILuminaria[]>();
    for (const l of entities) {
      const [lng, lat] = this.getBaseCoords(l);
      const key = `${lng.toFixed(6)},${lat.toFixed(6)}`;
      const arr = grupos.get(key);
      if (arr) arr.push(l);
      else grupos.set(key, [l]);
    }

    for (const grupo of grupos.values()) {
      if (grupo.length <= 1) continue; // Sin superposición → sin offset

      const [lng, lat] = this.getBaseCoords(grupo[0]);
      const n = grupo.length;

      // Radio del círculo: separa los vecinos ~OVERLAP_SEP_METERS sobre el arco
      const radiusM = Math.max(
        this.OVERLAP_MIN_RADIUS_METERS,
        (n * this.OVERLAP_SEP_METERS) / (2 * Math.PI),
      );

      // Conversión metros → grados (lat fija; lng según latitud)
      const mPorGradoLat = 111320;
      const mPorGradoLng =
        111320 * Math.cos((lat * Math.PI) / 180) || mPorGradoLat;

      // Orden estable para que cada luminaria mantenga su posición entre updates
      const ordenado = [...grupo].sort((a, b) =>
        this.getEntityId(a).localeCompare(this.getEntityId(b)),
      );

      ordenado.forEach((l, i) => {
        const ang = (2 * Math.PI * i) / n;
        const dLng = (radiusM * Math.cos(ang)) / mPorGradoLng;
        const dLat = (radiusM * Math.sin(ang)) / mPorGradoLat;
        this.overlapCoords.set(this.getEntityId(l), [lng + dLng, lat + dLat]);
      });
    }
  }

  /**
   * Override: precalcula los offsets de posiciones superpuestas antes de
   * generar los features (entityToFeature los consume).
   */
  override updateData(entities: ILuminaria[]): UpdateDataResult {
    // Las que no tienen una ubicación real no se dibujan.
    const conUbicacion = entities.filter(
      (l) => this.ubicacionEnMapa(l) !== null,
    );
    this.computeOverlapOffsets(conUbicacion);
    return super.updateData(conUbicacion);
  }

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte una luminaria a un Feature de GeoJSON
   */
  override entityToFeature(
    luminaria: ILuminaria,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(luminaria);

    // Determinar código de estado de la luminaria (0..5)
    const estado = this.determineEntityState(luminaria);

    // Obtener coordenadas: si comparte posición con otras, usar la calculada
    // con offset (computeOverlapOffsets); si no, la base de su ubicación.
    const coordinates =
      this.overlapCoords.get(this.getEntityId(luminaria)) ??
      this.getBaseCoords(luminaria);

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: luminaria._id,
        deveui: luminaria.deveui,
        identificacion: luminaria.identificacion,
        estado: estado,
        // Color para círculos (zoom bajo)
        color: this.stateToColorMap[estado],
        // Metadata para popups
        direccion: luminaria.direccion,
        tipoDispositivo: luminaria.tipoDispositivo,
        // Datos completos con flag mapaEspecifico incluido
      },
    };
  }

  /**
   * Determina el código de estado (0..5) de una luminaria.
   * Fuente única: determinarCodigoEstado (estado-luminaria.ts).
   */
  determineEntityState(luminaria: ILuminaria): CodigoEstadoLuminaria {
    return determinarCodigoEstado(luminaria);
  }

  /**
   * Expresión para el icono (dinámica según código de estado)
   */
  override getIconExpression(): string | mapboxgl.Expression {
    if (this.mapaEspecifico) {
      // Mapa específico: iconos .webp dinámicos según código
      const expression: mapboxgl.Expression = [
        'match',
        ['get', 'estado'],
        0,
        this.stateToIconMap[0],
        1,
        this.stateToIconMap[1],
        2,
        this.stateToIconMap[2],
        3,
        this.stateToIconMap[3],
        4,
        this.stateToIconMap[4],
        5,
        this.stateToIconMap[5],
        'luminaria-default-icon', // Default
      ];

      return expression;
    } else {
      // Mapa general: pin fijo (luminaria-default-icon usa pin-luminaria.png)

      return 'luminaria-default-icon';
    }
  }

  /**
   * Expresión para el color del círculo (en zoom bajo)
   */
  getCircleColorExpression(): mapboxgl.Expression {
    return [
      'match',
      ['get', 'estado'],
      0,
      this.stateToColorMap[0],
      1,
      this.stateToColorMap[1],
      2,
      this.stateToColorMap[2],
      3,
      this.stateToColorMap[3],
      4,
      this.stateToColorMap[4],
      5,
      this.stateToColorMap[5],
      this.stateToColorMap[5], // Default
    ];
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea todos los layers para luminarias
   */
  protected override createLayers(): void {
    if (!this.map) return;

    // 1. LAYERS DE CLUSTERING (con color por prioridad de estado)
    if (this.supportsClustering) {
      const clusterLayerId = `${this.entityType}-cluster-layer`;
      const clusterCountLayerId = `${this.entityType}-cluster-count-layer`;

      // Asegurar que el source clusterizado exista con clusterProperties adecuadas
      if (!this.map.getSource(this.clusteredSourceId)) {
        this.map.addSource(this.clusteredSourceId, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
          buffer: 128,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 40,
          // Conteo por código dentro de cada cluster (para el donut multicolor).
          clusterProperties: CLUSTER_COUNT_PROPERTIES,
        } as any);
      }

      // Layer de círculos del cluster (oculto: lo reemplaza el donut HTML).
      // Se mantiene como respaldo/zona de click para zoom.
      this.addLayer({
        id: clusterLayerId,
        type: 'circle',
        source: this.clusteredSourceId,
        filter: ['has', 'point_count'],
        layout: { visibility: 'none' },
        paint: {
          'circle-color': this.buildClusterColorExpression() as any,
          'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 50, 40],
          'circle-opacity': 0.7,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Layer de texto del cluster (oculto: el total va en el centro del donut).
      this.addLayer({
        id: clusterCountLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
          visibility: 'none',
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      // Unclustered layer
      this.addLayer({
        id: this.unclusteredLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-size': this.mapaEspecifico
            ? this.ICON_SIZE_ESPECIFICO
            : this.ICON_SIZE_GENERAL,
          'icon-allow-overlap': true,
          'icon-padding': 0,
          visibility: 'none',
        },
      });
    }

    // 2. LAYER DE ICONOS
    // En mapa general (mapaEspecifico=false): siempre visible
    // En mapa específico (mapaEspecifico=true): visible solo si zoom >= 14
    this.addLayer({
      id: this.iconLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': this.mapaEspecifico
          ? this.ICON_SIZE_ESPECIFICO
          : this.ICON_SIZE_GENERAL,
        'icon-allow-overlap': true,
        'icon-padding': 0,
        visibility: this.mapaEspecifico
          ? this.currentZoom >= this.zoomThreshold
            ? 'visible'
            : 'none'
          : 'visible', // Mapa general: siempre visible
      },
    });

    // 3. LAYER DE CÍRCULOS (zoom < 14)
    // Solo se usa en mapa específico (mapaEspecifico=true)
    // En mapa general (mapaEspecifico=false): siempre oculto
    this.addLayer({
      id: this.circleLayerId,
      type: 'circle',
      source: this.sourceId,
      paint: {
        'circle-radius': this.CIRCLE_RADIUS,
        'circle-color': this.getCircleColorExpression(),
        'circle-opacity': 0.8,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
      },
      layout: {
        visibility: this.mapaEspecifico
          ? this.currentZoom < this.zoomThreshold
            ? 'visible'
            : 'none'
          : 'none', // Mapa general: nunca visible
      },
    });

    // Setup de event listener para zoom
    this.setupZoomListener();

    // Setup de donut markers (cluster multicolor)
    this.setupDonutSync();
  }

  /**
   * Construye la expresión de color de clusters según el modo de mapa
   * - Mapa específico: prioridad por código (gana el más bajo)
   * - Mapa general: SIEMPRE amarillo (evita naranjas por datos incompletos)
   */
  private buildClusterColorExpression(): mapboxgl.Expression | string {
    if (!this.mapaEspecifico) {
      // Mapa general → constante amarillo
      return this.clusterColor as string;
    }

    // Mapa específico → color del código más bajo presente en el cluster.
    return [
      'case',
      ['>', ['get', 'count_0'], 0],
      this.stateToColorMap[0],
      ['>', ['get', 'count_1'], 0],
      this.stateToColorMap[1],
      ['>', ['get', 'count_2'], 0],
      this.stateToColorMap[2],
      ['>', ['get', 'count_3'], 0],
      this.stateToColorMap[3],
      ['>', ['get', 'count_4'], 0],
      this.stateToColorMap[4],
      ['>', ['get', 'count_5'], 0],
      this.stateToColorMap[5],
      this.clusterColor,
    ] as any;
  }

  /**
   * Actualiza el estilo (color) del layer de cluster cuando cambia mapaEspecifico
   */
  private updateClusterStyle(): void {
    if (!this.map) return;
    const clusterLayerId = `${this.entityType}-cluster-layer`;
    if (this.map.getLayer(clusterLayerId)) {
      this.map.setPaintProperty(
        clusterLayerId,
        'circle-color',
        this.buildClusterColorExpression() as any,
      );
    }
  }

  /**
   * Override: crear sources para incluir clusterProperties en clusteredSource
   */
  protected override createSources(): void {
    if (!this.map) return;

    // Source normal (sin clustering)
    // buffer alto: evita que los iconos (webp ~40px) se recorten en los bordes
    // de los tiles al hacer zoom (con buffer 0 desaparecían al acercarse).
    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        buffer: 128,
      });
    }

    // Source clustered con propiedades por estado
    if (
      this.supportsClustering &&
      !this.map.getSource(this.clusteredSourceId)
    ) {
      this.map.addSource(this.clusteredSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        buffer: 128,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 40,
        clusterProperties: CLUSTER_COUNT_PROPERTIES,
      } as any);
    }
  }

  // ========== DONUT MARKERS (CLUSTER MULTICOLOR) ==========

  // Sincroniza markers HTML tipo donut con los clusters visibles, en cada render.
  private setupDonutSync(): void {
    if (!this.map) return;
    // Detachar el handler previo antes de re-registrar: createLayers()/initialize()
    // se re-ejecuta en cada cambio de estilo sobre la MISMA instancia Map, y sin
    // esto se acumulaba un handler 'render' por switch (cada uno corre por frame → freeze).
    if (this.renderHandler) this.map.off('render', this.renderHandler);
    this.renderHandler = () => this.updateDonutMarkers();
    this.map.on('render', this.renderHandler);
  }

  private updateDonutMarkers(): void {
    if (!this.map) return;
    // Solo cuando el clustering está activo y el manager visible.
    // Donut multicolor solo en mapa específico; mapa general usa cluster nativo amarillo.
    if (!this.clusteringEnabled || !this.isVisible || !this.mapaEspecifico) {
      this.clearDonutMarkers();
      return;
    }
    if (!this.map.isSourceLoaded(this.clusteredSourceId)) return;

    const features = this.map.querySourceFeatures(this.clusteredSourceId);
    const nuevos: Record<string, mapboxgl.Marker> = {};

    for (const f of features) {
      const props = f.properties as Record<string, unknown> | null;
      if (!props || !props['cluster']) continue;
      const id = String(props['cluster_id']);
      if (nuevos[id]) continue;

      let marker = this.donutMarkers[id];
      if (!marker) {
        const coords = (f.geometry as GeoJSON.Point).coordinates as [
          number,
          number,
        ];
        const el = this.crearDonutElement(props);
        el.addEventListener('click', (ev) => {
          ev.stopPropagation();
          this.zoomACluster(Number(props['cluster_id']), coords);
        });
        marker = new mapboxgl.Marker({ element: el }).setLngLat(coords);
        this.donutMarkers[id] = marker;
      }
      nuevos[id] = marker;
      if (!this.donutMarkersOnScreen[id]) marker.addTo(this.map);
    }

    // Remover los que ya no están en pantalla (y limpiar cache para refrescar).
    for (const id in this.donutMarkersOnScreen) {
      if (!nuevos[id]) {
        this.donutMarkersOnScreen[id].remove();
        delete this.donutMarkers[id];
      }
    }
    this.donutMarkersOnScreen = nuevos;
  }

  // Click en cluster → acercar el mapa al nivel donde se expande.
  private zoomACluster(clusterId: number, coords: [number, number]): void {
    if (!this.map) return;
    const source = this.map.getSource(this.clusteredSourceId) as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (!source?.getClusterExpansionZoom) return;
    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err || zoom == null || !this.map) return;
      this.map.easeTo({ center: coords, zoom: zoom + 0.5, duration: 500 });
    });
  }

  // Construye el elemento HTML del donut a partir de los conteos por código.
  private crearDonutElement(props: Record<string, unknown>): HTMLElement {
    const counts = CODIGOS.map((c) => Number(props[`count_${c}`] ?? 0));
    const total =
      counts.reduce((a, b) => a + b, 0) || Number(props['point_count']) || 0;
    const colors = CODIGOS.map((c) => this.stateToColorMap[c]);

    const r = total >= 1000 ? 32 : total >= 100 ? 28 : total >= 10 ? 24 : 20;
    const r0 = Math.round(r * 0.6);
    const w = r * 2;
    const fontSize = total >= 1000 ? 14 : total >= 100 ? 13 : 12;

    let html = `<div><svg width="${w}" height="${w}" viewBox="0 0 ${w} ${w}" text-anchor="middle" style="font:${fontSize}px sans-serif; display:block">`;
    let offset = 0;
    for (let i = 0; i < counts.length; i++) {
      if (counts[i] <= 0) continue;
      html += this.donutSegment(
        offset / total,
        (offset + counts[i]) / total,
        r,
        r0,
        colors[i],
      );
      offset += counts[i];
    }
    html += `<circle cx="${r}" cy="${r}" r="${r0}" fill="white"/>`;
    html += `<text dominant-baseline="central" transform="translate(${r}, ${r})">${total}</text>`;
    html += `</svg></div>`;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const el = wrapper.firstChild as HTMLElement;
    el.style.cursor = 'pointer';
    return el;
  }

  private donutSegment(
    start: number,
    end: number,
    r: number,
    r0: number,
    color: string,
  ): string {
    if (end - start === 1) end -= 0.00001;
    const a0 = 2 * Math.PI * (start - 0.25);
    const a1 = 2 * Math.PI * (end - 0.25);
    const x0 = Math.cos(a0);
    const y0 = Math.sin(a0);
    const x1 = Math.cos(a1);
    const y1 = Math.sin(a1);
    const largeArc = end - start > 0.5 ? 1 : 0;
    return `<path d="M ${r + r0 * x0} ${r + r0 * y0} L ${r + r * x0} ${
      r + r * y0
    } A ${r} ${r} 0 ${largeArc} 1 ${r + r * x1} ${r + r * y1} L ${
      r + r0 * x1
    } ${r + r0 * y1} A ${r0} ${r0} 0 ${largeArc} 0 ${r + r0 * x0} ${
      r + r0 * y0
    }" fill="${color}"/>`;
  }

  private clearDonutMarkers(): void {
    for (const id in this.donutMarkersOnScreen) {
      this.donutMarkersOnScreen[id].remove();
    }
    this.donutMarkersOnScreen = {};
    this.donutMarkers = {};
  }

  // ========== ZOOM AWARENESS ==========

  /**
   * Configura el listener para cambios de zoom
   */
  private setupZoomListener(): void {
    if (!this.map) return;

    // Detachar el handler previo: se re-registra en cada cambio de estilo sobre la
    // misma instancia Map; sin esto se acumulaban handlers 'zoomend' por cada switch.
    if (this.zoomHandler) this.map.off('zoomend', this.zoomHandler);

    this.zoomHandler = () => {
      if (!this.map) return;
      const newZoom = this.map.getZoom();
      this.onZoomChange(newZoom);
    };

    // OPTIMIZACIÓN: Usar 'zoomend' en lugar de 'zoom' para evitar ejecutar en cada frame
    // 'zoom' se dispara 60 veces/seg → causa jank
    // 'zoomend' se dispara solo cuando termina el zoom → smooth
    this.map.on('zoomend', this.zoomHandler);
  }

  /**
   * Handler para cambios de zoom (implementa IZoomAware)
   */
  onZoomChange(zoom: number): void {
    const previousZoom = this.currentZoom;
    this.currentZoom = zoom;

    // Si cruzamos el threshold, cambiar visibilidad de layers
    const previousMode = previousZoom >= this.zoomThreshold;
    const currentMode = zoom >= this.zoomThreshold;

    if (previousMode !== currentMode) {
      // IMPORTANTE: Solo actualizar visibilidad basada en zoom si clustering está OFF
      // Cuando clustering ON, los layers normales (iconLayerId/circleLayerId) deben permanecer ocultos
      if (!this.clusteringEnabled) {
        this.updateZoomBasedVisibility();
      }
    }
  }

  /**
   * Actualiza la visibilidad de layers según el zoom
   * Solo aplica en mapa específico (mapaEspecifico=true)
   * En mapa general (mapaEspecifico=false), siempre muestra iconos
   * IMPORTANTE: Respeta this.isVisible para no mostrar layers cuando el filtro está apagado
   */
  private updateZoomBasedVisibility(): void {
    if (!this.map) return;

    if (this.mapaEspecifico) {
      // Mapa específico: alternar entre iconos y círculos según zoom (respetando filtro)
      const showIcons = this.currentZoom >= this.zoomThreshold;

      this.setLayerVisibility(this.iconLayerId, this.isVisible && showIcons);
      this.setLayerVisibility(this.circleLayerId, this.isVisible && !showIcons);
    } else {
      // Mapa general: siempre iconos, nunca círculos (respetando filtro)

      this.setLayerVisibility(this.iconLayerId, this.isVisible);
      this.setLayerVisibility(this.circleLayerId, false);
    }
  }

  // ========== CLUSTERING ==========

  /**
   * Maneja la visibilidad según clustering
   */
  protected override updateClusteringVisibility(): void {
    if (!this.map || !this.supportsClustering) return;

    if (this.clusteringEnabled) {
      if (this.mapaEspecifico) {
        // Mapa específico: cluster dibujado con donut HTML multicolor.
        // Circle/text nativos ocultos.
        this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
        this.setLayerVisibility(
          `${this.entityType}-cluster-count-layer`,
          false,
        );
      } else {
        // Mapa general: cluster circle nativo amarillo + count. Sin donut.
        this.setLayerVisibility(
          `${this.entityType}-cluster-layer`,
          this.isVisible,
        );
        this.setLayerVisibility(
          `${this.entityType}-cluster-count-layer`,
          this.isVisible,
        );
        this.clearDonutMarkers();
      }
      // Puntos sueltos (sin cluster) se siguen mostrando con sus íconos.
      this.setLayerVisibility(this.unclusteredLayerId, this.isVisible);
      this.setLayerVisibility(this.iconLayerId, false);
      this.setLayerVisibility(this.circleLayerId, false);
    } else {
      // CLUSTERING OFF
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
      this.setLayerVisibility(`${this.entityType}-cluster-count-layer`, false);
      this.setLayerVisibility(this.unclusteredLayerId, false);
      this.clearDonutMarkers();

      // Restaurar visibilidad según zoom (respetando filtro)
      // IMPORTANTE: updateZoomBasedVisibility ya respeta this.isVisible internamente
      this.updateZoomBasedVisibility();
    }
  }

  /**
   * Helper para cambiar visibilidad
   */
  private setLayerVisibility(layerId: string, visible: boolean): void {
    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  // ========== CLICKS Y POPUPS ==========

  //Ahora esto no se está usando, pero es un buen ejemplo de cómo agregar campos extra a los datos del popup
  /**
   * Override para transformar la entidad antes de mostrar el popup
   * Agrega mapaEspecifico para que el popup sepa si mostrar versión simple o completa
   */
  protected override getEntityForPopup(entity: ILuminaria): any {
    return {
      ...entity,
      mapaEspecifico: this.mapaEspecifico,
    };
  }

  // ========== CLEANUP ==========

  /**
   * Override de cleanup para remover event listeners
   */
  override cleanup(): void {
    // Remover zoom listener
    if (this.map && this.zoomHandler) {
      this.map.off('zoomend', this.zoomHandler);
    }

    // Remover render listener y donut markers
    if (this.map && this.renderHandler) {
      this.map.off('render', this.renderHandler);
    }
    this.clearDonutMarkers();

    // Resetear estados específicos de luminarias
    this.currentZoom = 15;
    this.mapaEspecifico = false; // Resetear a false para mapa general
    this.overlapCoords.clear();

    // Llamar al cleanup base
    super.cleanup();
  }

  /**
   * Override: en el cambio de estilo desconecta render/zoomend y limpia los donut
   * markers (DOM) SIN tocar sources/layers (setStyle ya los borró). Evita handlers
   * acumulados y markers huérfanos que se quedaban con cluster_id viejo comiéndose
   * los clicks (cluster que no expande).
   */
  override detachListeners(): void {
    if (this.map && this.zoomHandler) {
      this.map.off('zoomend', this.zoomHandler);
    }
    if (this.map && this.renderHandler) {
      this.map.off('render', this.renderHandler);
    }
    this.clearDonutMarkers();
  }

  // ========== VISIBILIDAD GENERAL ==========

  /**
   * Override de setVisibility
   */
  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    if (this.clusteringEnabled && this.supportsClustering) {
      if (this.mapaEspecifico) {
        // Mapa específico: donut HTML. Nativos ocultos.
        this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
        this.setLayerVisibility(
          `${this.entityType}-cluster-count-layer`,
          false,
        );
        if (!visible) this.clearDonutMarkers();
      } else {
        // Mapa general: cluster circle nativo amarillo + count.
        this.setLayerVisibility(`${this.entityType}-cluster-layer`, visible);
        this.setLayerVisibility(
          `${this.entityType}-cluster-count-layer`,
          visible,
        );
        this.clearDonutMarkers();
      }
      this.setLayerVisibility(this.unclusteredLayerId, visible);
    } else {
      if (this.mapaEspecifico) {
        // Mapa específico: según zoom, mostrar iconos o círculos
        if (this.currentZoom >= this.zoomThreshold) {
          this.setLayerVisibility(this.iconLayerId, visible);
          this.setLayerVisibility(this.circleLayerId, false);
        } else {
          this.setLayerVisibility(this.iconLayerId, false);
          this.setLayerVisibility(this.circleLayerId, visible);
        }
      } else {
        // Mapa general: siempre iconos, nunca círculos
        this.setLayerVisibility(this.iconLayerId, visible);
        this.setLayerVisibility(this.circleLayerId, false);
      }
    }
  }
}
